// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewPreviouslyRaised.test.tsx
//
// The findings of the earlier version's review that this version fixed, as
// warhol's version follow-up found them (spec dave.savostyanov/0088): what was
// flagged, what the newer version prints instead, and a way to disagree.
import { type AskPunttReviewResolved } from "@mg/schemas/src/christo/askPunttReview";
import { ArrowsLeftRight, CheckCircle } from "@phosphor-icons/react";
import { Badge, Button, Text } from "@radix-ui/themes";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { AskV2ReviewCommentSection } from "./AskV2ReviewCommentSection";

type AskV2ReviewPreviouslyRaisedProps = {
  /** The review's fixed findings; a reopened one is left out here. */
  resolved: readonly AskPunttReviewResolved[];
  /** This review's version number; the earlier one is one below it when `versionMark` cannot say. */
  versionNumber?: number;
  /** This review's id, so `versionMark` can name its version. */
  reviewId?: string;
  /**
   * The version a review of this artwork is ("V2"), or undefined when the
   * lineage does not carry it. why: a fixed finding names the review it was
   * raised on (`priorReviewId`), which is not always the one just before this.
   */
  versionMark?: (reviewId: string) => string | undefined;
  /** Opens the comparison with the earlier version on the finding's window. */
  onViewChange?: (entry: AskPunttReviewResolved) => void;
  /** Puts the finding back as an open comment; absent for a reader. */
  onReopen?: (commentId: string) => void;
  /** A fixed comment to open the section at and scroll to. */
  revealCommentId?: string | null;
  reopenPending?: boolean;
};

function Side({
  tone,
  label,
  text,
  testId,
}: {
  tone: "flagged" | "corrected";
  label: string;
  text: string;
  testId: string;
}) {
  return (
    <div
      className={`rounded-md px-2 py-1.5 text-[13px] ${
        tone === "flagged"
          ? "bg-puntt-red-2 text-puntt-red-11"
          : "bg-malachite-50 text-malachite-800"
      }`}
      data-testid={testId}
    >
      <span className="block text-[11px] font-semibold uppercase">{label}</span>
      <span className="break-words">{text}</span>
    </div>
  );
}

export function AskV2ReviewPreviouslyRaised({
  resolved,
  versionNumber,
  reviewId,
  versionMark,
  onViewChange,
  onReopen,
  reopenPending = false,
  revealCommentId = null,
}: AskV2ReviewPreviouslyRaisedProps) {
  const { t } = useTranslation("ui");
  const listId = useId();
  // Closed by default, like Approved and Rejected: these are decided too.
  const [open, setOpen] = useState(false);
  const shown = resolved.filter((entry) => !entry.reopenedAt);
  const revealing =
    // Stryker disable next-line ConditionalExpression: every listed entry has a string commentId, so with no id to reveal some() finds nothing either way
    revealCommentId != null &&
    shown.some((entry) => entry.commentId === revealCommentId);
  // "View comment" on a change that fixed one of these: open the section and
  // bring that entry into view.
  useEffect(() => {
    if (revealing) setOpen(true);
  }, [revealing, revealCommentId]);
  useEffect(() => {
    // Stryker disable next-line ConditionalExpression,LogicalOperator: when not revealing the id names no rendered entry and while closed no entry is rendered, so the lookup finds nothing and running on changes nothing; forcing the guard true is killed by "opens at a fixed comment it is asked to reveal and scrolls it into view"
    if (!revealing || !open) return;
    document
      .querySelector(
        `[data-testid="ask-v2-review-resolved-${revealCommentId}"]`,
      )
      ?.scrollIntoView?.({ block: "nearest" });
  }, [open, revealCommentId, revealing]);
  if (shown.length === 0) return null;

  const newer =
    (reviewId ? versionMark?.(reviewId) : undefined) ??
    (versionNumber
      ? `V${versionNumber}`
      : t("askV2.review.resolvedThisVersion"));
  const olderByNumber =
    versionNumber && versionNumber > 1
      ? `V${versionNumber - 1}`
      : t("askV2.review.resolvedEarlierVersion");
  const olderOf = (entry: AskPunttReviewResolved) =>
    versionMark?.(entry.priorReviewId) ?? olderByNumber;

  return (
    <AskV2ReviewCommentSection
      sectionKey="previously-raised"
      label={t("askV2.review.sectionPreviouslyRaised")}
      count={shown.length}
      open={open}
      listId={listId}
      onToggle={() => setOpen((was) => !was)}
    >
      {shown.map((entry) => {
        const older = olderOf(entry);
        return (
          <li
            key={entry.commentId}
            className="mb-2 rounded-lg border border-puntt-neutral-gray-5 bg-base-white px-3 py-2"
            data-testid={`ask-v2-review-resolved-${entry.commentId}`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Text as="p" size="2" weight="medium" className="min-w-0 flex-1">
                {entry.issue}
              </Text>
              <Badge color="green" variant="soft" size="1">
                <CheckCircle size={12} weight="fill" aria-hidden="true" />
                {t("askV2.review.resolvedIn", { version: newer })}
              </Badge>
              <Badge color="gray" variant="soft" size="1">
                {t("askV2.review.resolvedAiVerified")}
              </Badge>
            </div>
            <Text as="p" size="1" color="gray" className="mt-0.5">
              {t("askV2.review.resolvedRaisedIn", { version: older })}
              {entry.reason ? ` · ${entry.reason}` : ""}
            </Text>
            {entry.flaggedText || entry.correctedText ? (
              <div className="mt-2 grid gap-1.5">
                {entry.flaggedText ? (
                  <Side
                    tone="flagged"
                    label={t("askV2.review.resolvedFlagged", {
                      version: older,
                    })}
                    text={entry.flaggedText}
                    testId={`ask-v2-review-resolved-flagged-${entry.commentId}`}
                  />
                ) : null}
                {entry.correctedText ? (
                  <Side
                    tone="corrected"
                    label={t("askV2.review.resolvedCorrected", {
                      version: newer,
                    })}
                    text={entry.correctedText}
                    testId={`ask-v2-review-resolved-corrected-${entry.commentId}`}
                  />
                ) : null}
              </div>
            ) : null}
            {entry.sourceQuote ? (
              <Text
                as="p"
                size="1"
                color="gray"
                className="mt-1.5"
                data-testid={`ask-v2-review-resolved-source-${entry.commentId}`}
              >
                {t("askV2.review.resolvedSource")}: “{entry.sourceQuote}”
              </Text>
            ) : null}
            <div className="mt-2 flex flex-wrap gap-2">
              {onViewChange && entry.windows.length > 0 ? (
                <Button
                  variant="soft"
                  size="1"
                  data-testid={`ask-v2-review-resolved-view-change-${entry.commentId}`}
                  onClick={() => onViewChange(entry)}
                >
                  <ArrowsLeftRight size={14} aria-hidden="true" />
                  {t("askV2.review.resolvedViewChange", {
                    before: older,
                    after: newer,
                  })}
                </Button>
              ) : null}
              {onReopen ? (
                <Button
                  variant="outline"
                  color="gray"
                  size="1"
                  disabled={reopenPending}
                  data-testid={`ask-v2-review-resolved-reopen-${entry.commentId}`}
                  onClick={() => onReopen(entry.commentId)}
                >
                  {t("askV2.review.resolvedReopen")}
                </Button>
              ) : null}
            </div>
          </li>
        );
      })}
      <li>
        <Text
          as="p"
          size="1"
          color="gray"
          className="px-1"
          data-testid="ask-v2-review-previously-raised-note"
        >
          {t("askV2.review.resolvedNote")}
        </Text>
      </li>
    </AskV2ReviewCommentSection>
  );
}
